import type { AuditEntry } from './audit';
import { remove, send } from './client';
import type { Role, Scope, ScopeKind } from './common';
import { query, type Cursor, type CursorPage, type OffsetPage } from './paging';

/** A user as GET /users lists it. `email` is masked ("a•••@…") for an account without `pii.read`. */
export interface User {
  id: number;
  email: string;
  name: string;
  role: Role;
  /** null: an inactive operator whose location was removed (it needs one before it can be activated) */
  scope: Scope | null;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  /** devices signed in right now */
  sessionCount: number;
  /** two-step verification is on (absent on an older backend) */
  totpEnabled?: boolean;
  /** a Hub operator's invitation is still open or expired unused: it can be sent again */
  invitePending?: boolean;
}
/** GET /users/:id: the row plus the last five things the person did and who made the account. */
export interface UserDetail extends User {
  recentActivity: AuditEntry[];
  createdBy?: { id: number; name: string } | null;
}

/**
 * PATCH /users/:id. Access is sent as role + where it reaches: `provinceCode` null = national (admin, viewer,
 * regulator), `locationId` for an operator.
 */
export interface UserPatch { name?: string; role?: Role; provinceCode?: string | null; locationId?: number; active?: boolean }
/** POST /users: no password = the server generates one, answered once. */
export interface NewUserInput { email: string; name: string; role: Role; provinceCode?: string; locationId?: number; password?: string }
/** POST /users: the new user, and the generated password when none was given (shown once, never stored). */
export interface CreatedUser { user: User; password?: string }

/** One device signed in to someone's account (GET /users/:id/sessions). IP and device are null when masked. */
export interface UserSession { id: string; createdAt: string; expiresAt: string; ip: string | null; userAgent: string | null }

export type UserStatus = 'active' | 'inactive';
export interface UserFilter { page?: number; limit?: number; q?: string; role?: Role | ''; scope?: ScopeKind | ''; provinceCode?: string; status?: UserStatus | '' }
export type UserPage = OffsetPage<User>;

export const userPaths = {
  list: (f: UserFilter = {}) => `/users${query({ ...f })}`,
  detail: (id: number) => `/users/${id}`,
  sessions: (id: number, page = 1, limit?: number) => `/users/${id}/sessions${query({ page, limit })}`,
  /** what the person did, newest first (the /audit shape), for an actor who may manage them */
  activity: (id: number, cursor: Cursor, limit: number) => `/users/${id}/activity${query({ ...cursor, limit })}`,
};
export type UserSessionPage = OffsetPage<UserSession>;
export type UserActivityPage = CursorPage<AuditEntry>;

export const createUser = (body: NewUserInput) => send<CreatedUser>('/users', body);
export const updateUser = (id: number, body: UserPatch) => send<User>(`/users/${id}`, body, 'PATCH');
/** `password` is generated here, so a retry after a lost answer sets the same one (the server echoes it). */
export const resetUserPassword = (id: number, password: string) => send<{ password: string }>(`/users/${id}/reset-password`, { password });
/** Only an account that never signed in can be deleted (409 IN_USE otherwise). */
export const deleteUser = (id: number) => remove(`/users/${id}`);
/** Turns the user's two-step verification off and signs them out everywhere; they enroll again when the policy asks. */
export const resetUserTotp = (id: number) => send<void>(`/users/${id}/totp/reset`);
export const signOutUserEverywhere = (id: number) => send<{ signedOut: number }>(`/users/${id}/sessions/sign-out-all`);
